import { useEffect, useState } from "react";
import { Mail, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { authedFetch } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const RecoveryEmailManager = ({ onSessionVerified }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [enrolCode, setEnrolCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [sessionEmail, setSessionEmail] = useState("");
  const [sessionCode, setSessionCode] = useState("");
  const [sessionSent, setSessionSent] = useState(false);

  const load = async () => {
    const response = await authedFetch("/security/recovery-emails");
    const result = response.ok ? await response.json() : { emails: [] };
    const rows = result.emails || [];
    setContacts(rows);
    setSessionEmail((current) => current || rows[0]?.email || "");
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const sendEnrolment = async () => {
    const response = await authedFetch("/security/recovery-emails/send", { method: "POST", body: JSON.stringify({ email }) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return toast.error(result.detail || "Could not send the verification code.");
    if (result.already_verified) return toast.info("That email address is already verified.");
    setPendingEmail(email.trim().toLowerCase());
    setEnrolCode("");
    toast.success("Verification code sent to the additional email.");
  };

  const verifyEnrolment = async () => {
    const response = await authedFetch("/security/recovery-emails/verify", { method: "POST", body: JSON.stringify({ email: pendingEmail, code: enrolCode }) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return toast.error(result.detail || "The verification code is incorrect.");
    setEmail(""); setPendingEmail(""); setEnrolCode("");
    toast.success("Recovery email verified.");
    load();
  };

  const remove = async (value) => {
    const response = await authedFetch("/security/recovery-emails", { method: "DELETE", body: JSON.stringify({ email: value }) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return toast.error(result.detail || "Could not remove the recovery email.");
    toast.success("Recovery email removed.");
    load();
  };

  const sendSessionCode = async () => {
    const response = await authedFetch("/security/email/send", { method: "POST", body: JSON.stringify({ email: sessionEmail }) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return toast.error(result.detail || "Could not send the email verification code.");
    setSessionSent(true); setSessionCode("");
    toast.success("Verification code sent.");
  };

  const verifySession = async () => {
    const response = await authedFetch("/security/email/verify", { method: "POST", body: JSON.stringify({ code: sessionCode }) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) return toast.error(result.detail || "The verification code is incorrect.");
    setSessionSent(false); setSessionCode("");
    onSessionVerified?.();
    toast.success("Email verification is active for this protected session.");
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading verified emails…</div>;
  return <div className="space-y-3 rounded-md border border-slate-200 p-3" data-testid="recovery-email-manager">
    <div><p className="text-sm font-semibold text-slate-900">Verified email fallbacks</p><p className="text-xs text-slate-500">Keep up to two verified email addresses. Your account email counts as the first.</p></div>
    <div className="space-y-2">{contacts.map((contact) => <div key={contact.email} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm"><span className="flex min-w-0 items-center gap-2"><Mail className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate">{contact.email}</span>{contact.primary && <span className="text-xs text-slate-400">Primary</span>}</span>{!contact.primary && <Button size="icon" variant="ghost" onClick={() => remove(contact.email)} aria-label={`Remove ${contact.email}`}><X className="h-4 w-4" /></Button>}</div>)}</div>
    {contacts.length < 2 && !pendingEmail && <div className="flex gap-2"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Second recovery email" /><Button type="button" variant="outline" onClick={sendEnrolment} disabled={!email.trim()}>Verify email</Button></div>}
    {pendingEmail && <div className="rounded-md border border-blue-200 bg-blue-50 p-3"><p className="text-xs text-blue-900">Enter the 6 digit code sent to {pendingEmail}.</p><div className="mt-2 flex gap-2"><Input inputMode="numeric" value={enrolCode} onChange={(e) => setEnrolCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" /><Button type="button" onClick={verifyEnrolment} disabled={enrolCode.length !== 6}>Confirm</Button></div></div>}
    {contacts.length > 0 && <div className="border-t border-slate-100 pt-3"><p className="text-xs font-medium text-slate-600">Verify a protected session by email</p>{!sessionSent ? <div className="mt-2 flex gap-2"><select value={sessionEmail} onChange={(e) => setSessionEmail(e.target.value)} className="h-10 min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 text-sm">{contacts.map((contact) => <option key={contact.email} value={contact.email}>{contact.email}</option>)}</select><Button type="button" variant="outline" onClick={sendSessionCode}>Send code</Button></div> : <div className="mt-2 flex gap-2"><Input inputMode="numeric" value={sessionCode} onChange={(e) => setSessionCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Email code" /><Button type="button" onClick={verifySession} disabled={sessionCode.length !== 6}>Verify</Button></div>}</div>}
  </div>;
};
